/**
=========================================================
* Sagres Rotas React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Sagres Rotas React components

// Sagres Rotas React example components
import Sidenav from "examples/Sidenav";

// Sagres Rotas React themes
import theme from "assets/theme";

// Sagres Rotas React Dark Mode themes
import themeDark from "assets/theme-dark";

// Sagres Rotas React routes
import routes from "routes";

// Sagres Rotas React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import { Observer, Provider } from "mobx-react";
import authStore from "stores/auth";
import { CircularProgress } from "@mui/material";
import stores from "./store_list";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  useEffect(() => {
    authStore.setSessaoGuidId(window.localStorage.getItem("sessaoGuidId"));
    authStore.setRefreshTokenSessao(window.localStorage.getItem("refreshTokenSessao"));
    if (authStore.sessaoGuidId) {
      authStore
        .checarSessao()
        .finally(() => {
          authStore.setAppLoaded();
          if (pathname === "/" || pathname === "/login") {
            if (authStore.usuario) {
              navigate("/mapa");
            } else {
              navigate("/login");
            }
          }
        })
        .catch(() => {
          authStore.setAppLoaded();
          navigate("/login");
        });
    } else {
      authStore.setAppLoaded();
      navigate("/login");
    }
  }, []);

  return (
    <Observer>
      {() => {
        if (authStore.appLoaded) {
          return (
            <Provider {...stores}>
              <ThemeProvider theme={darkMode ? themeDark : theme}>
                <CssBaseline />
                {layout === "dashboard" && (
                  <Sidenav
                    color={sidenavColor}
                    brand={
                      (transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite
                    }
                    brandName="Sagres Rotas"
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                )}
                <Routes>
                  {getRoutes(routes)}
                  <Route path="*" element={<Navigate to="/mapa" />} />
                </Routes>
              </ThemeProvider>
            </Provider>
          );
        }
        return <CircularProgress />;
      }}
    </Observer>
  );
}
